/**
 * Change History Utilities
 *
 * Pure functions for computing field-level diffs between old and new form values.
 * Used by editable hooks to generate change history entries after successful mutations.
 */
import { SelectProps } from '@amzn/awsui-components-core';
import {
  ChangeEntityType,
  ChangeSectionName,
  ChangeOperationType,
  FieldChange,
} from 'src/types/change-history/changeHistory.types';
import { CreateChangeHistoryInput, createChangeHistoryAPI } from 'src/services/api/changeHistoryService';
import { logger } from './logger';

// Re-export field label maps from the centralized constants file
export {
  OVERVIEW_FIELD_LABELS,
  KEY_DATES_FIELD_LABELS,
  MANUFACTURING_FIELD_LABELS,
  MARKET_FIELD_LABELS,
  SPECIFICATIONS_FIELD_LABELS,
  FINANCIAL_FIELD_LABELS,
} from 'src/constants/changeHistoryConstants';

// ============================================================================
// Section Filter Options
// ============================================================================

const ALL_SECTIONS_OPTION: SelectProps.Option = { value: 'all', label: 'All Sections' };

/**
 * Builds grouped section options based on entity type (RPN vs FPN).
 * RPN has: Overview (Status, Program Overview, Docs, Contacts), Attributes, Key Dates,
 *          Additional Info (Manufacturing, Market, Specs), AGM (Versions, Assortments, SKU)
 * FPN has: Overview (Program Overview), Attributes, Key Dates,
 *          Additional Info (Specs, Financial Info), AGM (SKU)
 */
export const buildSectionOptions = (entityType: ChangeEntityType): SelectProps.Options => {
  if (entityType === 'fpn') {
    return [
      ALL_SECTIONS_OPTION,
      {
        label: 'Overview',
        options: [{ value: 'overview', label: 'Program Overview' }],
      },
      {
        label: 'Attributes',
        options: [
          { value: 'standard_attributes', label: 'Standard Attributes' },
          { value: 'product_line_attributes', label: 'Product Line Attributes' },
          { value: 'program_attributes', label: 'Program Attributes' },
        ],
      },
      {
        label: 'Country & Key Dates',
        options: [
          { value: 'street_dates', label: 'Street Dates' },
          { value: 'other_key_dates', label: 'Other Key Dates' },
        ],
      },
      {
        label: 'Additional Info',
        options: [
          { value: 'specifications', label: 'Specifications' },
          { value: 'financial_info', label: 'Financial Information' },
        ],
      },
      {
        label: 'AGM',
        options: [{ value: 'agm_skus', label: 'AGM SKUs' }],
      },
    ];
  }

  // RPN sections
  return [
    ALL_SECTIONS_OPTION,
    {
      label: 'Overview',
      options: [
        { value: 'overview', label: 'Program Overview' },
        { value: 'program_status', label: 'Program Status' },
        { value: 'documents_links', label: 'Documents & Links' },
      ],
    },
    {
      label: 'Attributes',
      options: [
        { value: 'standard_attributes', label: 'Standard Attributes' },
        { value: 'product_line_attributes', label: 'Product Line Attributes' },
        { value: 'program_attributes', label: 'Program Attributes' },
      ],
    },
    {
      label: 'Country & Key Dates',
      options: [{ value: 'other_key_dates', label: 'Other Key Dates' }],
    },
    {
      label: 'Additional Info',
      options: [
        { value: 'manufacturing_info', label: 'Manufacturing' },
        { value: 'market_info', label: 'Market' },
        { value: 'specifications', label: 'Specifications' },
      ],
    },
    {
      label: 'AGM',
      options: [
        { value: 'agm_versions', label: 'AGM Versions' },
        { value: 'agm_assortments', label: 'AGM Assortments' },
        { value: 'agm_skus', label: 'AGM SKUs' },
      ],
    },
  ];
};

// ============================================================================
// Core Diff Function
// ============================================================================

/**
 * Compares two objects and returns an array of field-level changes.
 * Only includes fields that actually changed (using string comparison for safety).
 *
 * @param initialValues - The "before" state (Formik initialValues snapshot)
 * @param currentValues - The "after" state (Formik current values)
 * @param fieldLabelMap - Map of field_name → human-readable label
 * @param fieldsToTrack - Optional list of specific fields to track. If omitted, all keys in currentValues are compared.
 * @returns Array of FieldChange objects for fields that changed
 */
export function computeFieldChanges(
  initialValues: Record<string, unknown>,
  currentValues: Record<string, unknown>,
  fieldLabelMap: Record<string, string>,
  fieldsToTrack?: string[]
): FieldChange[] {
  const changes: FieldChange[] = [];
  const keys = fieldsToTrack || Object.keys(currentValues);

  for (const key of keys) {
    const oldVal = initialValues[key];
    const newVal = currentValues[key];

    // Normalize values for comparison
    const oldStr = formatValueForComparison(oldVal);
    const newStr = formatValueForComparison(newVal);

    // Only track if the value actually changed
    if (oldStr !== newStr) {
      const oldDisplay = formatValueForDisplay(oldVal);
      const newDisplay = formatValueForDisplay(newVal);
      const suffix = getChangeOperationSuffix(oldDisplay, newDisplay);
      changes.push({
        field_name: key,
        field_label: (fieldLabelMap[key] || key) + suffix,
        old_value: oldDisplay,
        new_value: newDisplay,
      });
    }
  }

  return changes;
}

/**
 * Computes field changes for Formik select/dropdown fields that use { value, label } objects.
 * Extracts the label for display and the value for comparison.
 */
export function computeSelectFieldChanges(
  initialValues: Record<string, { value?: string; label?: string } | string | null>,
  currentValues: Record<string, { value?: string; label?: string } | string | null>,
  fieldLabelMap: Record<string, string>
): FieldChange[] {
  const changes: FieldChange[] = [];

  for (const key of Object.keys(currentValues)) {
    const oldVal = initialValues[key];
    const newVal = currentValues[key];

    // Extract comparison values
    const oldCompare = typeof oldVal === 'object' && oldVal !== null ? oldVal.value || '' : String(oldVal || '');
    const newCompare = typeof newVal === 'object' && newVal !== null ? newVal.value || '' : String(newVal || '');

    if (oldCompare !== newCompare) {
      // Use label for display, fall back to value
      const oldDisplay =
        typeof oldVal === 'object' && oldVal !== null ? oldVal.label || oldVal.value || '' : String(oldVal || '');
      const newDisplay =
        typeof newVal === 'object' && newVal !== null ? newVal.label || newVal.value || '' : String(newVal || '');

      changes.push({
        field_name: key,
        field_label: fieldLabelMap[key] || key,
        old_value: oldDisplay || null,
        new_value: newDisplay || null,
      });
    }
  }

  return changes;
}

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Normalizes a value for consistent comparison
 */
function formatValueForComparison(value: unknown): string {
  if (value === null || value === undefined) return '';
  if (typeof value === 'boolean') return value ? 'true' : 'false';
  if (Array.isArray(value)) return JSON.stringify([...value].sort());
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value).trim();
}

/**
 * Formats a value for human-readable display in the change history UI
 */
function formatValueForDisplay(value: unknown): string | null {
  if (value === null || value === undefined || value === '') return null;
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (Array.isArray(value)) return value.filter(Boolean).join(', ') || null;
  if (typeof value === 'object') {
    // Handle { value, label } select objects
    const obj = value as Record<string, unknown>;
    if (obj.label) return String(obj.label);
    if (obj.value) return String(obj.value);
    return JSON.stringify(value);
  }
  return String(value);
}

/**
 * Returns a suffix label based on old/new values to indicate the type of change.
 * - "(added)" when old is empty and new has a value
 * - "(deleted)" when old has a value and new is empty
 * - "(updated)" when both old and new have values
 */
export function getChangeOperationSuffix(oldValue: unknown, newValue: unknown): string {
  const hasOld = oldValue !== null && oldValue !== undefined && oldValue !== '';
  const hasNew = newValue !== null && newValue !== undefined && newValue !== '';

  if (!hasOld && hasNew) return ' (added)';
  if (hasOld && !hasNew) return ' (deleted)';
  if (hasOld && hasNew) return ' (updated)';
  return '';
}

/**
 * Logs change history entry creation (fire-and-forget) and catches errors silently.
 * This wraps the API call to ensure it never block or fails the parent mutation.
 */
export function logChangeHistoryFireAndForget(apiCall: () => Promise<unknown>, context: string): void {
  apiCall().catch((error) => {
    // Silently log — never block the user's save operation
    logger.warn(`Change history log failed for ${context}`, { error });
  });
}

// ============================================================================
// DRY Helper — reduces boilerplate for the repeated fire-and-forget pattern
// ============================================================================

/**
 * Common parameters shared by most change history log calls.
 * Extracts the repeated fields to reduce boilerplate.
 */
interface LogChangeHistoryParams {
  entityType: ChangeEntityType;
  rpnId?: number;
  fpnId?: number;
  productLineId?: number;
  sectionName: ChangeSectionName;
  operationType: ChangeOperationType;
  createdBy: string;
  fieldChanges: FieldChange[];
  /** Optional override for record_count (defaults to fieldChanges.length) */
  recordCount?: number;
}

/**
 * Convenience wrapper that builds the CreateChangeHistoryInput from typed parameters
 * and fires the createChangeHistory API call as fire-and-forget.
 *
 * ```
 *
 * Usage:
 * ```
 * logChangeHistory({
 *   entityType: ChangeEntityType.RPN,
 *   rpnId,
 *   productLineId,
 *   sectionName: ChangeSectionName.OVERVIEW,
 *   operationType: ChangeOperationType.UPDATE,
 *   createdBy: user?.userAlias || '',
 *   fieldChanges: changes,
 * });
 * ```
 */
export function logChangeHistory(params: LogChangeHistoryParams): void {
  const { entityType, rpnId, fpnId, productLineId, sectionName, operationType, createdBy, fieldChanges, recordCount } =
    params;

  if (fieldChanges.length === 0) return;

  const input: CreateChangeHistoryInput = {
    entity_type: entityType,
    rpn_id: rpnId ?? null,
    fpn_id: fpnId ?? null,
    product_line_id: productLineId ?? null,
    section_name: sectionName,
    operation_type: operationType,
    created_by: createdBy,
    field_changes: JSON.stringify(fieldChanges),
    record_count: recordCount ?? fieldChanges.length,
  };

  const entityLabel = fpnId ? `FPN ${fpnId}` : rpnId ? `RPN ${rpnId}` : `Entity`;
  const context = `${entityLabel} ${sectionName}`;

  logChangeHistoryFireAndForget(() => createChangeHistoryAPI(input), context);
}
